import React from 'react';
import { Box, Button, Card, FormControl, FormHelperText, IconButton, InputLabel, OutlinedInput, useTheme } from "@mui/material";
import Logo from '../../../components/Logo';
import { useNavigate } from 'react-router-dom';
import { useUserProvider } from '../../../providers/useUserProvider';
import { useSnackbar } from 'notistack';
import MaterialIcon from '../../../components/MaterialIcon';
import { useForm } from 'react-hook-form';
import { FISCAL_CODE_OR_VAT_REGEX } from '../../../config/regex';
import { login } from '../../../api/auth';

type FormValues = {
    username: string;
    password: string;
}

const Login: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { register, handleSubmit, formState, getValues } = useForm<FormValues>({
        defaultValues: {
            username: "",
            password: ""
        },
    });
    const { errors } = formState;
    const navigate = useNavigate();
    const { setUserId, setUserType } = useUserProvider();
    const theme = useTheme();

    const [isRequestInProgress, setIsRequestInProgress] = React.useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = React.useState(false);

    const onSubmit = async () => {
        const username = getValues("username");
        const password = getValues("password");

        if (isRequestInProgress) {
            return;
        }

        try {
            setIsRequestInProgress(true);

            const response = await login({ username, password });

            enqueueSnackbar("Login effettuato con successo", { variant: "success" });

            setUserId(response.data.utente.id_utente);
            setUserType(response.data.utente.tipo_utente);

            sessionStorage.setItem("token", response.data.token);

            navigate("/dashboard");
        } catch (error: any) {
            enqueueSnackbar(error.message, { variant: "error" });
        } finally {
            setIsRequestInProgress(false);
        }
    }

    const handleTogglePasswordVisibility = () => {
        setIsPasswordVisible(!isPasswordVisible);
    }

    return (
        <Box
            className="LoginPage"
            alignItems="center"
            width="100svw"
            height="100svh"
            display="flex"
            justifyContent="center"
            bgcolor={theme.palette.background.default}>
            <Card
                sx={{
                    maxWidth: "28rem",
                    margin: "1rem",
                    boxSizing: "border-box",
                    padding: "2rem",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
                variant='outlined'>
                <Logo style={{ height: "4rem" }} />
                <Box height="2rem" />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormControl fullWidth variant='outlined' error={!!errors.username}>
                        <InputLabel htmlFor="username">Codice Fiscale o Partita IVA</InputLabel>
                        <OutlinedInput
                            id="username"
                            type="text"
                            fullWidth
                            label="Codice Fiscale o Partita IVA"
                            {...register("username", {
                                required: "Il campo è obbligatorio",
                                pattern: FISCAL_CODE_OR_VAT_REGEX
                            })}
                        />
                        {errors.username && (
                            <FormHelperText>{errors.username.message}</FormHelperText>
                        )}
                    </FormControl>
                    <Box height="1rem" />
                    <FormControl fullWidth variant='outlined' sx={{ marginBottom: "2rem" }} error={!!errors.password}>
                        <InputLabel htmlFor="password">Password</InputLabel>
                        <OutlinedInput
                            id="password"
                            type={isPasswordVisible ? "text" : "password"}
                            fullWidth
                            endAdornment={
                                <IconButton
                                    edge="end"
                                    onClick={handleTogglePasswordVisibility}
                                >
                                    {!isPasswordVisible ? <MaterialIcon icon="visibility" /> : <MaterialIcon icon="visibility_off" />}
                                </IconButton>
                            }
                            label="Password"
                            {...register("password", {
                                required: "Il campo è obbligatorio",
                            })}
                        />
                        {errors.password && (
                            <FormHelperText>{errors.password.message}</FormHelperText>
                        )}
                    </FormControl>
                    <FormControl fullWidth>
                        <Button variant="contained" type="submit" disabled={isRequestInProgress}>Accedi</Button>
                    </FormControl>
                </form>
            </Card>
        </Box >
    );
}

export default Login;
